/**
 * iter-307: Pricing-Model Preview Dialog
 *
 * Shows BOQ items SIDE-BY-SIDE in both pricing models (V→C and C→V) so
 * the user can spot-check tier maths before publishing. Eliminates the
 * "wait, my rate was 1000?!" support pings.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowRight, ArrowLeft } from 'lucide-react';

const formatINR = (n) => `Rs. ${Math.round(+n || 0).toLocaleString('en-IN')}`;

const tierChip = (label, value, color) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${color}`}>
    <span className="opacity-70">{label}</span>
    <span>{formatINR(value)}</span>
  </span>
);

export function PricingModelPreviewDialog({ open, onOpenChange, items, worksheet }) {
  const adminPct = +(worksheet?.admin_markup_percentage ?? 20) || 0;
  const clientPct = +(worksheet?.client_markup_percentage ?? 20) || 0;
  const currentModel = worksheet?.pricing_model === 'client_to_vendor' ? 'C→V' : 'V→C';

  // Derive all 3 tier rates for every item irrespective of current pricing_model.
  // Uses estimated `rate` (treated as the current model's tier) and cascades.
  const rows = (items || [])
    .filter(it => it && it.item_type !== 'header' && !it.is_discount)
    .map((it, idx) => {
      const qty = +(it.quantity ?? 0) || 0;
      const rate = +(it.rate ?? 0) || 0;
      // Always derive vendor, admin, client rates regardless of current model
      let vendor, admin, client;
      if (worksheet?.pricing_model === 'client_to_vendor') {
        client = rate;
        admin = clientPct ? client / (1 + clientPct / 100) : client;
        vendor = adminPct ? admin / (1 + adminPct / 100) : admin;
      } else {
        vendor = rate;
        admin = vendor * (1 + adminPct / 100);
        client = admin * (1 + clientPct / 100);
      }
      // Also derive actual tier rates. iter-338c (Msg-680): ALWAYS recompute
      // actAdmin / actClient from CURRENT worksheet markups instead of trusting
      // the cached actual_admin_rate / actual_client_rate on the item — those
      // were frozen with whatever markup % was active at write-time and break
      // SSOT when the user later changes admin_markup_percentage /
      // client_markup_percentage. The vendor (or client, depending on model)
      // anchor is the only source of truth.
      const actQty = it.actual_quantity ?? qty;
      const rawAct = +(it.actual_rate ?? 0) || 0;
      let actVendor = 0, actAdmin = 0, actClient = 0;
      if (worksheet?.pricing_model === 'client_to_vendor') {
        // Anchor: client (the rate user typed in `actual_rate` for C→V)
        const anchor = rawAct || +(it.actual_client_rate ?? 0) || 0;
        actClient = anchor;
        actAdmin = clientPct ? actClient / (1 + clientPct / 100) : actClient;
        actVendor = adminPct ? actAdmin / (1 + adminPct / 100) : actAdmin;
      } else {
        // Anchor: vendor (the rate user typed in `actual_rate` for V→C)
        const anchor = rawAct || +(it.actual_vendor_rate ?? 0) || 0;
        actVendor = anchor;
        actAdmin = actVendor * (1 + adminPct / 100);
        actClient = actAdmin * (1 + clientPct / 100);
      }
      return {
        idx: idx + 1,
        name: it.task_title || it.description || `Item ${idx + 1}`,
        unit: it.unit || 'Nos',
        qty,
        vendor, admin, client,
        actQty,
        actVendor: actVendor || 0,
        actAdmin: actAdmin || 0,
        actClient: actClient || 0,
        // iter-338d (Msg-680 Bug 4): for ad-hoc rows where Est qty=0/rate=0,
        // surface the Actual tier line so per-row math reconciles with the
        // footer "Actual basic totals". `displayQty` is what the # column
        // shows; tier columns show the line subtotal at each tier.
        displayQty: qty || actQty,
        rowVendor: (qty && vendor) ? qty * vendor : actQty * (actVendor || 0),
        rowAdmin: (qty && admin) ? qty * admin : actQty * (actAdmin || 0),
        rowClient: (qty && client) ? qty * client : actQty * (actClient || 0),
      };
    });

  const totals = rows.reduce((a, r) => ({
    vendor: a.vendor + r.qty * r.vendor,
    admin: a.admin + r.qty * r.admin,
    client: a.client + r.qty * r.client,
    actVendor: a.actVendor + r.actQty * r.actVendor,
    actAdmin: a.actAdmin + r.actQty * r.actAdmin,
    actClient: a.actClient + r.actQty * r.actClient,
  }), { vendor: 0, admin: 0, client: 0, actVendor: 0, actAdmin: 0, actClient: 0 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="pricing-model-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Pricing-Model Preview</span>
            <span className="text-xs px-2 py-0.5 bg-stone-100 rounded text-stone-600 font-normal">
              Current model: <strong>{currentModel}</strong> · Admin {adminPct}% · Client {clientPct}%
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Verify tier maths land at expected client totals before publishing. Both pricing models are derived from the same vendor cost so totals always reconcile.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto border border-stone-200 rounded-md" data-testid="pricing-model-preview-table-wrap">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-stone-50 border-b-2 border-stone-300">
              <tr>
                <th className="p-2 text-left font-semibold text-stone-700 w-10">#</th>
                <th className="p-2 text-left font-semibold text-stone-700">Item</th>
                <th className="p-2 text-center font-semibold text-stone-700 w-14">Qty</th>
                <th colSpan="3" className="p-2 text-center font-semibold text-purple-800 bg-purple-50 border-l border-stone-300">
                  Vendor → Client <ArrowRight size={11} className="inline ml-1" />
                </th>
                <th colSpan="3" className="p-2 text-center font-semibold text-blue-800 bg-blue-50 border-l border-stone-300">
                  <ArrowLeft size={11} className="inline mr-1" /> Client → Vendor
                </th>
              </tr>
              <tr className="border-b border-stone-300">
                <th></th><th></th><th></th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-purple-50/40">Vendor</th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-purple-50/40">Admin (+{adminPct}%)</th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-purple-50/40 border-r border-stone-300">Client (+{clientPct}%)</th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-blue-50/40">Client</th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-blue-50/40">Admin (-{clientPct}%)</th>
                <th className="p-1.5 text-right font-medium text-stone-600 bg-blue-50/40">Vendor (-{adminPct}%)</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-8 text-stone-400 italic">No items to preview.</td></tr>
              ) : (
                rows.map(r => (
                  <tr key={r.idx} className="border-b border-stone-100 hover:bg-amber-50/40" data-testid={`pricing-preview-row-${r.idx}`}>
                    <td className="p-2 text-stone-500">{r.idx}</td>
                    <td className="p-2 text-stone-800 max-w-md truncate" title={r.name}>{r.name}</td>
                    <td className="p-2 text-center text-stone-600">{r.displayQty}</td>
                    {/* V→C */}
                    <td className="p-2 text-right text-stone-700 bg-purple-50/30">{formatINR(r.rowVendor)}</td>
                    <td className="p-2 text-right text-stone-700 bg-purple-50/30">{formatINR(r.rowAdmin)}</td>
                    <td className="p-2 text-right font-semibold text-purple-900 bg-purple-100/60 border-r border-stone-300">{formatINR(r.rowClient)}</td>
                    {/* C→V */}
                    <td className="p-2 text-right font-semibold text-blue-900 bg-blue-100/60">{formatINR(r.rowClient)}</td>
                    <td className="p-2 text-right text-stone-700 bg-blue-50/30">{formatINR(r.rowAdmin)}</td>
                    <td className="p-2 text-right text-stone-700 bg-blue-50/30">{formatINR(r.rowVendor)}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="sticky bottom-0 bg-stone-100 border-t-2 border-stone-400">
              <tr className="font-semibold text-stone-800">
                <td colSpan="3" className="p-2 text-right">GRAND TOTAL (basic)</td>
                <td className="p-2 text-right bg-purple-100">{formatINR(totals.vendor)}</td>
                <td className="p-2 text-right bg-purple-100">{formatINR(totals.admin)}</td>
                <td className="p-2 text-right text-purple-900 bg-purple-200 border-r border-stone-400" data-testid="pricing-preview-vtc-client-total">{formatINR(totals.client)}</td>
                <td className="p-2 text-right text-blue-900 bg-blue-200" data-testid="pricing-preview-ctv-client-total">{formatINR(totals.client)}</td>
                <td className="p-2 text-right bg-blue-100">{formatINR(totals.admin)}</td>
                <td className="p-2 text-right bg-blue-100">{formatINR(totals.vendor)}</td>
              </tr>
              {(totals.actVendor + totals.actAdmin + totals.actClient) > 0 && (
                <tr className="text-stone-700 border-t border-stone-300">
                  <td colSpan="3" className="p-2 text-right italic">Actual (site) basic totals</td>
                  <td className="p-2 text-right bg-purple-50">{formatINR(totals.actVendor)}</td>
                  <td className="p-2 text-right bg-purple-50">{formatINR(totals.actAdmin)}</td>
                  <td className="p-2 text-right text-purple-900 bg-purple-100 border-r border-stone-400">{formatINR(totals.actClient)}</td>
                  <td className="p-2 text-right text-blue-900 bg-blue-100">{formatINR(totals.actClient)}</td>
                  <td className="p-2 text-right bg-blue-50">{formatINR(totals.actAdmin)}</td>
                  <td className="p-2 text-right bg-blue-50">{formatINR(totals.actVendor)}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        <div className="text-[11px] text-stone-500 pt-2 leading-relaxed">
          <strong>How to read:</strong> the same vendor cost cascades upward (V→C, left) or the same client price cascades downward (C→V, right). Both columns end at the same Client total — that's the price the client sees on the Published BOQ.
        </div>
      </DialogContent>
    </Dialog>
  );
}
